/**
 * Phase 125.C — Individualize this unit (per-lesson overlay).
 *
 * Two entry paths:
 *   • From a unit view: hit /individualize-unit?unit=<id> — teacher
 *     picks a saved plan or types the goal inline, generates overlays.
 *   • From an Individual Student Plan: hit
 *     /individualize-unit?plan=<id> — teacher picks the unit, the
 *     plan's goal + accommodations pre-fill.
 *
 * Privacy framing matches Phase 125.B: anonymous label only, no name
 * fields, no student data stored unless the teacher hits Save.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Download, Eye, EyeOff, Layers, Loader2, Save, Sparkles, Trash2,
} from "lucide-react";
import AccommodationPicker from "../components/AccommodationPicker";

const apiBase = () => `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function UnitOverlay() {
  const [searchParams] = useSearchParams();
  const initialUnit = searchParams.get("unit") || "";
  const initialPlan = searchParams.get("plan") || "";

  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(true);
  const [unitId, setUnitId] = useState(initialUnit);

  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [planId, setPlanId] = useState(initialPlan);

  // Inline plan fields (optional — overrides the linked plan when set)
  const [learningGoal, setLearningGoal] = useState("");
  const [expectationCodes, setExpectationCodes] = useState("");
  const [accommodations, setAccommodations] = useState({ selectedIds: [], otherText: "" });
  const [optionalDetail, setOptionalDetail] = useState("");

  // Output / save / export
  const [payload, setPayload] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [branded, setBranded] = useState(true);
  const [studentLabel, setStudentLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedOverlays, setSavedOverlays] = useState([]);

  // Fetch teacher's units (anything with output_type === "unit")
  useEffect(() => {
    (async () => {
      setLoadingUnits(true);
      try {
        const res = await fetch(`${apiBase()}/activities`, { credentials: "include" });
        if (!res.ok) throw new Error(`Failed to load units (${res.status})`);
        const data = await res.json();
        const list = (data.activities || data || [])
          .filter((a) => (a.output_type || "").toLowerCase() === "unit");
        setUnits(list);
      } catch (e) {
        toast.error(e?.message || "Couldn't load units.");
      } finally {
        setLoadingUnits(false);
      }
    })();
  }, []);

  // Fetch teacher's saved plans
  useEffect(() => {
    (async () => {
      setLoadingPlans(true);
      try {
        const res = await fetch(`${apiBase()}/individual-student-plan/list`, {
          credentials: "include",
        });
        if (res.status === 402) { setPlans([]); return; }
        if (!res.ok) throw new Error(`Failed to load plans (${res.status})`);
        const data = await res.json();
        setPlans(data.plans || []);
      } catch (e) {
        toast.error(e?.message || "Couldn't load saved plans.");
      } finally {
        setLoadingPlans(false);
      }
    })();
    refreshSavedOverlays();
  }, []);

  // If launched with ?plan=<id> pre-fill plan fields too
  useEffect(() => {
    if (!initialPlan) return;
    (async () => {
      try {
        const res = await fetch(`${apiBase()}/individual-student-plan/${initialPlan}`, {
          credentials: "include",
        });
        if (!res.ok) return;
        const doc = await res.json();
        setLearningGoal(doc.learning_goal || "");
        setExpectationCodes((doc.expectation_codes || []).join(", "));
        setAccommodations({
          selectedIds: doc.accommodation_ids || [],
          otherText: doc.accommodation_other || "",
        });
        setOptionalDetail(doc.optional_detail || "");
        setStudentLabel(doc.student_label || "");
      } catch { /* silent — picker still lets them choose */ }
    })();
  }, [initialPlan]);

  const refreshSavedOverlays = async () => {
    try {
      const res = await fetch(`${apiBase()}/unit-overlay/list`, {
        credentials: "include",
      });
      if (res.status === 402) { setSavedOverlays([]); return; }
      if (!res.ok) return;
      const data = await res.json();
      setSavedOverlays(data.overlays || []);
    } catch { /* silent */ }
  };

  const generate = async () => {
    if (!unitId) {
      toast.error("Pick a unit first.");
      return;
    }
    if (!planId && !learningGoal.trim()) {
      toast.error("Link a saved plan OR enter a learning goal.");
      return;
    }
    setGenerating(true);
    setPayload(null);
    try {
      const body = {
        unit_activity_id: unitId,
        student_plan_id: planId || null,
        learning_goal: learningGoal.trim() || null,
        expectation_codes: expectationCodes.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean),
        accommodation_ids: accommodations.selectedIds || [],
        accommodation_other: accommodations.otherText || "",
        optional_detail: optionalDetail.trim(),
      };
      const res = await fetch(`${apiBase()}/unit-overlay/generate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.status === 402) {
        toast.error("Paid feature. Upgrade to use unit overlays.");
        return;
      }
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || `Generate failed (${res.status})`);
      }
      setPayload(await res.json());
      toast.success("Overlays generated.");
    } catch (e) {
      toast.error(e?.message || "Generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  const exportPdf = async () => {
    if (!payload) return;
    setExporting(true);
    try {
      const res = await fetch(`${apiBase()}/unit-overlay/export`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          payload, branded,
          student_label: studentLabel || "Student",
        }),
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `unit-overlay${branded ? "" : "-unbranded"}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("PDF downloaded.");
    } catch (e) {
      toast.error(e?.message || "PDF export failed.");
    } finally {
      setExporting(false);
    }
  };

  const saveOverlay = async () => {
    if (!payload) return;
    const label = (studentLabel || `Student ${savedOverlays.length + 1}`).trim();
    setSaving(true);
    try {
      const res = await fetch(`${apiBase()}/unit-overlay/save`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ student_label: label, payload }),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      toast.success(`Saved as "${label}".`);
      setStudentLabel("");
      refreshSavedOverlays();
    } catch (e) {
      toast.error(e?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const loadSavedOverlay = async (overlayId) => {
    try {
      const res = await fetch(`${apiBase()}/unit-overlay/${overlayId}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Load failed (${res.status})`);
      const doc = await res.json();
      setUnitId(doc.unit_activity_id);
      setLearningGoal(doc.learning_goal || "");
      setExpectationCodes((doc.expectation_codes || []).join(", "));
      setAccommodations({
        selectedIds: doc.accommodation_ids || [],
        otherText: doc.accommodation_other || "",
      });
      setOptionalDetail(doc.optional_detail || "");
      setStudentLabel(doc.student_label || "");
      setPayload({
        unit_activity_id: doc.unit_activity_id,
        unit_name: doc.unit_name,
        grade: doc.grade,
        subject: doc.subject,
        learning_goal: doc.learning_goal,
        expectation_codes: doc.expectation_codes,
        accommodation_ids: doc.accommodation_ids,
        accommodation_other: doc.accommodation_other,
        optional_detail: doc.optional_detail,
        overlays: doc.overlays,
      });
      toast.success(`Loaded "${doc.student_label}"`);
    } catch (e) {
      toast.error(e?.message || "Load failed.");
    }
  };

  const deleteSavedOverlay = async (overlayId, label) => {
    if (!window.confirm(`Delete "${label}"? This can't be undone.`)) return;
    try {
      const res = await fetch(`${apiBase()}/unit-overlay/${overlayId}`, {
        method: "DELETE", credentials: "include",
      });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      toast.success(`Deleted "${label}".`);
      refreshSavedOverlays();
    } catch (e) {
      toast.error(e?.message || "Delete failed.");
    }
  };

  const canGenerate = useMemo(
    () => !!unitId && (!!planId || !!learningGoal.trim()),
    [unitId, planId, learningGoal],
  );

  return (
    <div className="min-h-screen bg-[#f9fafb] text-[#1c2d3d]">
      <header className="border-b border-[#4a6984]/10 bg-white">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link
              to="/dashboard"
              data-testid="overlay-back"
              className="inline-flex items-center text-sm text-[#4a6984]/80 hover:text-[#1c2d3d]"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Link>
            <h1 className="text-xl font-extrabold tracking-tight flex items-center gap-2">
              <Layers className="h-5 w-5" /> Individualize this unit
            </h1>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-5 pb-3">
          <p className="text-xs text-[#4a6984]/80 italic">
            Pick a unit + a student plan (or enter the goal inline). The
            student does the SAME task as the class — this overlay
            adjusts the scaffold, expectation load, and checkpoint per
            lesson. No student data is stored unless you save under an
            anonymous label.
          </p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Left — form */}
        <section className="ae-card p-5 space-y-4" data-testid="overlay-form">
          <label className="text-xs font-bold block">
            Unit
            {loadingUnits ? (
              <div className="mt-1 text-xs text-[#4a6984]/60">Loading…</div>
            ) : units.length === 0 ? (
              <div className="mt-1 text-xs text-[#4a6984]/60">
                No unit plans yet. Create one in the Generator first.
              </div>
            ) : (
              <select
                value={unitId}
                onChange={(e) => setUnitId(e.target.value)}
                data-testid="overlay-unit-select"
                className="mt-1 w-full px-2.5 py-1.5 rounded border border-[#4a6984]/20 text-sm"
              >
                <option value="">— Pick a unit —</option>
                {units.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.activity_name}
                  </option>
                ))}
              </select>
            )}
          </label>

          <label className="text-xs font-bold block">
            Reuse a saved student plan (optional)
            {loadingPlans ? (
              <div className="mt-1 text-xs text-[#4a6984]/60">Loading…</div>
            ) : (
              <select
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
                data-testid="overlay-plan-select"
                className="mt-1 w-full px-2.5 py-1.5 rounded border border-[#4a6984]/20 text-sm"
              >
                <option value="">— Enter goal inline instead —</option>
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.student_label} · {p.subject}
                  </option>
                ))}
              </select>
            )}
            <span className="text-[10px] text-[#4a6984]/60 font-normal italic block mt-0.5">
              Linked plan's goal + accommodations will be used.
            </span>
          </label>

          <label className="text-xs font-bold block">
            Learning goal {planId ? "(override — leave blank to use plan's)" : ""}
            <textarea
              value={learningGoal}
              onChange={(e) => setLearningGoal(e.target.value)}
              rows={3}
              placeholder='e.g., "The student will identify the main idea in a short text using a graphic organizer."'
              data-testid="overlay-learning-goal"
              className="mt-1 w-full px-2.5 py-2 rounded border border-[#4a6984]/20 text-sm"
            />
          </label>

          <label className="text-xs font-bold block">
            Linked expectation code(s)
            <input
              type="text"
              value={expectationCodes}
              onChange={(e) => setExpectationCodes(e.target.value)}
              placeholder="e.g., C2.4, C3.5"
              data-testid="overlay-expectations"
              className="mt-1 w-full px-2.5 py-1.5 rounded border border-[#4a6984]/20 text-sm"
            />
          </label>

          <div>
            <div className="text-xs font-bold mb-1.5">
              Accommodations {planId ? "(override — leave empty to use plan's)" : ""}
            </div>
            <AccommodationPicker
              selectedIds={accommodations.selectedIds}
              otherText={accommodations.otherText}
              onChange={setAccommodations}
              testIdPrefix="overlay-accommodation"
            />
          </div>

          <label className="text-xs font-bold block">
            Optional detail
            <textarea
              value={optionalDetail}
              onChange={(e) => setOptionalDetail(e.target.value)}
              rows={2}
              placeholder="Current level, interests… No student name needed."
              data-testid="overlay-detail"
              className="mt-1 w-full px-2.5 py-2 rounded border border-[#4a6984]/20 text-sm"
            />
          </label>

          <button
            type="button"
            onClick={generate}
            disabled={!canGenerate || generating}
            data-testid="overlay-generate"
            className="ae-cta inline-flex items-center justify-center w-full px-4 py-2.5 rounded-md text-sm font-extrabold disabled:opacity-40"
          >
            {generating ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Generating overlays…</>
            ) : (
              <><Sparkles className="h-4 w-4 mr-2" /> Generate overlays</>
            )}
          </button>
        </section>

        {/* Right — output + library */}
        <section className="space-y-5">
          <div className="ae-card p-5" data-testid="overlay-output">
            {!payload && !generating ? (
              <div className="text-center py-12 text-[#4a6984]/60 text-sm">
                Your generated overlays will appear here.
              </div>
            ) : null}
            {generating ? (
              <div className="text-center py-12">
                <Loader2 className="h-6 w-6 animate-spin mx-auto text-[#4a6984]" />
                <p className="mt-3 text-sm text-[#4a6984]">
                  Generating per-lesson overlays for the unit…
                </p>
              </div>
            ) : null}
            {payload ? (
              <div className="space-y-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wide font-extrabold text-[#4a6984]/80 mb-1">Unit</div>
                  <p className="text-sm font-bold" data-testid="overlay-unit-name">{payload.unit_name}</p>
                  <p className="text-xs text-[#4a6984]/70">
                    {payload.grade} · {payload.subject}
                  </p>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide font-extrabold text-[#4a6984]/80 mb-1">Goal</div>
                  <p className="text-sm" data-testid="overlay-goal">{payload.learning_goal}</p>
                </div>
                <div className="space-y-2">
                  {(payload.overlays || []).map((o) => (
                    <details
                      key={o.lesson_number}
                      className="border border-[#4a6984]/15 rounded-md bg-white"
                      data-testid={`overlay-lesson-${o.lesson_number}`}
                    >
                      <summary className="cursor-pointer px-3 py-2 text-sm font-bold flex items-center justify-between hover:bg-[#96ded1]/10">
                        <span>Lesson {o.lesson_number} — {o.lesson_title}</span>
                      </summary>
                      <div className="px-3 pb-3 pt-1 space-y-2 text-xs">
                        <p><strong>Same task as class:</strong> {o.same_task_summary}</p>
                        <p><strong>Applied strategies:</strong> {o.applied_strategies}</p>
                        <p><strong>Reduced load:</strong> {o.reduced_load}</p>
                        <p><strong>Checkpoint:</strong> {o.checkpoint}</p>
                      </div>
                    </details>
                  ))}
                </div>

                <div className="border-t border-[#4a6984]/10 pt-3 flex flex-wrap items-center gap-2">
                  <label className="inline-flex items-center gap-1.5 text-xs text-[#4a6984]">
                    <input
                      type="checkbox"
                      checked={branded}
                      onChange={(e) => setBranded(e.target.checked)}
                      data-testid="overlay-branded-toggle"
                      className="h-3.5 w-3.5"
                    />
                    {branded ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                    Branded
                  </label>
                  <button
                    type="button"
                    onClick={exportPdf}
                    disabled={exporting}
                    data-testid="overlay-export"
                    className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-bold border border-[#4a6984]/20 hover:bg-[#96ded1]/10 disabled:opacity-40"
                  >
                    {exporting ? (
                      <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Exporting…</>
                    ) : (
                      <><Download className="h-3 w-3 mr-1" /> Download PDF</>
                    )}
                  </button>
                  <div className="flex items-center gap-1.5 ml-auto">
                    <input
                      type="text"
                      value={studentLabel}
                      onChange={(e) => setStudentLabel(e.target.value)}
                      placeholder="Anonymous label"
                      data-testid="overlay-student-label"
                      className="px-2 py-1 rounded border border-[#4a6984]/20 text-xs w-36"
                    />
                    <button
                      type="button"
                      onClick={saveOverlay}
                      disabled={saving}
                      data-testid="overlay-save"
                      className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-bold border border-[#4a6984]/20 hover:bg-[#96ded1]/10 disabled:opacity-40"
                    >
                      {saving ? (
                        <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Saving…</>
                      ) : (
                        <><Save className="h-3 w-3 mr-1" /> Save</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <div className="ae-card p-4" data-testid="overlay-library">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xs font-extrabold uppercase tracking-wide">
                My unit overlays
              </h2>
              <span className="text-[10px] text-[#4a6984]/60">
                {savedOverlays.length} saved
              </span>
            </div>
            {savedOverlays.length === 0 ? (
              <p className="text-xs text-[#4a6984]/70 py-3">
                No saved overlays yet. Generate and click Save to keep one
                under an anonymous label.
              </p>
            ) : (
              <ul className="divide-y divide-[#4a6984]/10">
                {savedOverlays.map((o) => (
                  <li
                    key={o.id}
                    data-testid={`overlay-library-row-${o.id}`}
                    className="flex items-center justify-between py-2 gap-2"
                  >
                    <button
                      type="button"
                      onClick={() => loadSavedOverlay(o.id)}
                      data-testid={`overlay-library-load-${o.id}`}
                      className="text-left flex-1 hover:bg-[#96ded1]/10 rounded px-2 py-1 -ml-2"
                    >
                      <div className="text-sm font-bold">{o.student_label}</div>
                      <div className="text-[10px] text-[#4a6984]/70">
                        {o.unit_name} · {o.grade} · {o.subject}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteSavedOverlay(o.id, o.student_label)}
                      data-testid={`overlay-library-delete-${o.id}`}
                      className="p-1 rounded text-[#4a6984]/60 hover:text-rose-600 hover:bg-rose-50"
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
